import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import { uploadUrl } from '../utils/uploads';
import PageHeader, { btnPrimary, btnSecondary, cardClass, inputClass } from '../components/PageHeader';

const DOCUMENT_LABELS = {
  coi: 'Certificate of Insurance (COI)',
  business_license: 'Business license',
  background_check_auth: 'Background check authorization',
};

const CHECK_ICON = (
  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs font-bold">
    ✓
  </span>
);

const MISSING_ICON = (
  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-400 text-xs">
    —
  </span>
);

const isImage = (filename) => /\.(jpe?g|png|webp|gif)$/i.test(filename || '');

const VendorApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveNote, setApproveNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [acting, setActing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/admin/vendors/${id}/onboarding`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.message || 'Failed to load application'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async () => {
    setActing(true);
    try {
      await apiFetch(`/admin/vendors/${id}/approve-onboarding`, {
        method: 'POST',
        body: JSON.stringify({ note: approveNote.trim() || null }),
      });
      toast.success('Vendor approved — they can now access jobs');
      navigate('/vendor-applications');
    } catch (err) {
      toast.error(err.message || 'Approve failed');
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Enter a reason for rejection');
      return;
    }
    setActing(true);
    try {
      await apiFetch(`/admin/vendors/${id}/reject-onboarding`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      toast.success('Application rejected');
      navigate('/vendor-applications');
    } catch (err) {
      toast.error(err.message || 'Reject failed');
    } finally {
      setActing(false);
    }
  };

  if (loading) return <p className="text-slate-500">Loading application…</p>;
  if (!data) return null;

  const { vendor, documents, agreement, checklist } = data;
  const pending = vendor.onboarding_status === 'pending_approval';

  return (
    <div>
      <Link to="/vendor-applications" className="text-sm text-teal-700 hover:underline">
        ← Back to applications
      </Link>

      <PageHeader
        title={vendor.display_name}
        subtitle={`Vendor application · ${vendor.email}`}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <section className={`${cardClass} p-6`}>
          <h2 className="font-semibold text-slate-900 mb-4">Applicant</h2>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Business name</dt>
              <dd className="font-medium text-slate-800 text-right">{vendor.display_name}</dd>
            </div>
            {vendor.business_name && (
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Legal name</dt>
                <dd className="font-medium text-slate-800 text-right">{vendor.business_name}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Email</dt>
              <dd className="font-medium text-slate-800 text-right">{vendor.email}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Phone</dt>
              <dd className="font-medium text-slate-800 text-right">{vendor.phone || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Contact name</dt>
              <dd className="font-medium text-slate-800 text-right">{vendor.user_name || '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Status</dt>
              <dd className="font-medium text-slate-800 text-right capitalize">
                {vendor.onboarding_status?.replace(/_/g, ' ')}
              </dd>
            </div>
          </dl>
        </section>

        <section className={`${cardClass} p-6`}>
          <h2 className="font-semibold text-slate-900 mb-4">Checklist</h2>
          <ul className="space-y-3 text-sm">
            {checklist.documents.map((item) => (
              <li key={item.type} className="flex items-center gap-3">
                {item.uploaded ? CHECK_ICON : MISSING_ICON}
                <span className={item.uploaded ? 'text-slate-800' : 'text-slate-500'}>
                  {item.label}
                </span>
              </li>
            ))}
            <li className="flex items-center gap-3 pt-1 border-t border-slate-100">
              {checklist.agreement.status === 'signed' ? CHECK_ICON : MISSING_ICON}
              <span>
                Vendor agreement
                {checklist.agreement.status === 'signed' && (
                  <span className="text-green-700 ml-2">
                    Signed
                    {checklist.agreement.signed_at &&
                      ` · ${new Date(checklist.agreement.signed_at).toLocaleDateString()}`}
                  </span>
                )}
                {agreement?.provider && (
                  <span className="text-slate-400 ml-1">({agreement.provider})</span>
                )}
              </span>
            </li>
          </ul>
        </section>
      </div>

      <section className={`${cardClass} p-6 mt-6`}>
        <h2 className="font-semibold text-slate-900 mb-4">Uploaded documents</h2>
        {documents.length === 0 ? (
          <p className="text-sm text-slate-500">No documents uploaded.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="border border-slate-200 rounded-lg p-4 flex flex-col gap-3"
              >
                <div>
                  <p className="font-medium text-slate-800 text-sm">
                    {DOCUMENT_LABELS[doc.document_type] || doc.document_type}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {doc.original_filename || 'Document'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(doc.uploaded_at).toLocaleString()}
                  </p>
                </div>
                {isImage(doc.original_filename) && (
                  <a href={uploadUrl(doc.url)} target="_blank" rel="noreferrer">
                    <img
                      src={uploadUrl(doc.url)}
                      alt={doc.original_filename}
                      className="h-28 w-full object-cover rounded border border-slate-100"
                    />
                  </a>
                )}
                <a
                  href={uploadUrl(doc.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-teal-700 font-medium hover:underline mt-auto"
                >
                  View / download
                </a>
              </div>
            ))}
          </div>
        )}
      </section>

      {pending && (
        <section className={`${cardClass} p-6 mt-6`}>
          <h2 className="font-semibold text-slate-900 mb-4">Decision</h2>
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Internal note <span className="text-slate-400 font-normal">(optional, on approve)</span>
              </label>
              <textarea
                className={`${inputClass} min-h-[80px]`}
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="e.g. Verified COI expires Dec 2026"
              />
              <button
                type="button"
                onClick={approve}
                disabled={acting}
                className={`${btnPrimary} mt-3 disabled:opacity-50`}
              >
                Approve vendor
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Rejection reason <span className="text-red-600">(required to reject)</span>
              </label>
              <textarea
                className={`${inputClass} min-h-[80px]`}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Tell the vendor what to fix or why they were declined"
              />
              <button
                type="button"
                onClick={reject}
                disabled={acting}
                className={`${btnSecondary} mt-3 disabled:opacity-50`}
              >
                Reject application
              </button>
            </div>
          </div>
        </section>
      )}

      {!pending && (
        <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          This application is no longer pending (status: {vendor.onboarding_status}).
          {vendor.rejected_reason && (
            <p className="mt-2">
              <span className="font-medium">Rejection reason:</span> {vendor.rejected_reason}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorApplicationDetail;
